import {
  type JSX,
  Ref,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DebugContext } from '../context/DebugContext';
import {
  renderDiffs,
  stripTags,
  textToParagraph,
  copyRichText,
  extractMarkdownFromCodeBlocks,
} from '../utils/utils';
import { SVGIcon } from './SVGIcon';
import { PrimaryButton } from './Buttons/Buttons';
import { RoundedButton } from '../styles/CustomButtons';
import ThumbUpIconAlt from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownIconAlt from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIconFilled from '@mui/icons-material/ThumbUp';
import ThumbDownIconFilled from '@mui/icons-material/ThumbDown';
import { Check } from '@mui/icons-material';
import { InlineCommentBox } from './InlineCommentBox';
import EditDiffComponent from './Admin/EditDiffComponent';
import { Colors } from '../styles/styles';
import { ConditionalTooltip } from './ConditionalTooltip';
import APIService from '../services/APIService';
import { useAccessToken, useUserState, useTroubleWords, useFeatureFlag } from '@features/user';
import { useAuditEnabled } from '@features/jotaudit';
import { useUserPreferences } from '@features/user-preferences';
import {
  AttestationEditIndicator,
  AttestationEditPopover,
  ComplianceRuleIndicator,
  ComplianceRulePopover,
} from '@features/jotcoach';
import { useJotCoachRules } from '@features/jotcoach/store/hooks';
import EditWithAI from './Note/EditWithAI';
import { ContentPasteGo, MoreHoriz } from '@mui/icons-material';
import { useDeleteModal } from '../context/modals/DeleteModalContext';
import { useMediaQuery } from '@mui/material';
import { useUIState } from '../context/uiState';
import { TutorialStep } from './Tutorial/TutorialStep';
import { useTutorial } from './Tutorial/TutorialContext';
import { useSnackbar } from 'notistack';
import { useEventTracking } from '@services/statsig';
import { useParams } from 'react-router-dom';

import type { BlockNoteEditor } from '@blocknote/core';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import '../styles/blockNoteStyles.css';
import { marked } from 'marked';
import removeMd from 'remove-markdown';
import { EditorView } from './BlockNote/EditorView';
import type { OnBracketClick, BracketMatch } from './BlockNoteBracketHighlightPlugin';
import { BracketReplacementPopover } from './BlockNote/BracketReplacementPopover';
import { useDomSelectionAnalytics } from '../hooks/useDomSelectionAnalytics';
import { useTextareaSelectionAnalytics } from '../hooks/useTextareaSelectionAnalytics';
import {
  buildSelectionCopied,
  buildSectionRendered,
  buildSectionInteracted,
  buildSectionEdited,
  type TelemetryContext,
} from '../utils/analytics/noteTelemetryBuilder';
import { trackNoteTelemetry } from '../utils/analytics/noteTelemetry';
import { useSectionRendered, useSectionInteracted } from '../hooks/analytics/useSectionAnalytics';

export const NoteSection = ({
  showTooltips = false,
  heading,
  text,
  markdownMode = false,
  sectionId,
  moduleId,
  isEdited,
  isRated,
  isAdded,
  isErrored,
  handleAdd,
  handleSaveEdits,
  handleRevertEdits,
  handleRegenerateSection,
  handleRequestAIEdit,
  handleSectionRatingFeedback,
  handleSectionCommentFeedback,
  handleSectionSetCopyStatus,
  handleSectionSendCopyEvents,
  handleStartModuleCustomizationSession,
  context,
  forceDebug,
  isSaving,
  edits,
  onWordClick,
  misspelledWords,
  isEditable = true,
  setTutorialText,
  showChromeExtensionTooltips = false,
  startingTutorialStep,
  onEditorReady,
  encounterId,
  complianceRuleIds,
  attestationEdits,
  onAppendText,
  enableBracketHighlight,
  onBracketClick,
}: {
  showTooltips?: boolean;
  heading: string;
  text: string;
  markdownMode?: boolean;
  sectionId?: string;
  moduleId?: string;
  isEdited: boolean;
  isRated?: boolean;
  isAdded: boolean;
  isErrored?: boolean;
  handleAdd: () => void;
  handleSaveEdits?: (editedText: string) => void;
  handleRevertEdits?: () => void;
  handleRegenerateSection?: (
    moduleId: string,
    setAwaitingRegenerationTaskId: (taskId: string) => void,
  ) => void;
  handleRequestAIEdit?: (
    sectionText: string,
    editInstructions: string,
    moduleId: string,
    setEditTaskResult: (taskId: string) => void,
  ) => void;
  handleSectionRatingFeedback?: (rating: number) => void;
  handleSectionCommentFeedback?: (comment: string, ai_instruction_session_id: string) => void;
  handleSectionSetCopyStatus?: () => void;
  handleSectionSendCopyEvents?: (module_id: string) => void;
  handleStartModuleCustomizationSession?: (userFeedback?: string) => void;
  context?: string;
  forceDebug?: boolean;
  isSaving: boolean;
  edits?: any;
  onWordClick?: (word: string) => void;
  misspelledWords?: string[];
  isEditable?: boolean;
  setTutorialText?: (headerText: string, description: string) => void;
  showChromeExtensionTooltips?: boolean;
  startingTutorialStep?: number;
  onEditorReady?: (editor: BlockNoteEditor) => void;
  encounterId?: string;
  complianceRuleIds?: string[];
  attestationEdits?: Array<{
    note_section: string;
    rationale: string;
    rule: string;
    text: string;
    title: string;
  }>;
  onAppendText?: (sectionId: string, newContent: string) => Promise<void>;
  enableBracketHighlight?: boolean;
  onBracketClick?: OnBracketClick;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { noteSection: noteSectionMetrics } = useEventTracking();
  const { noteId } = useParams();

  // Check if JotCoach feature is enabled
  const isJotCoachEnabled = useFeatureFlag('jotcoach_billing_analysis');
  // Check if audit features are enabled
  const isAuditEnabled = useAuditEnabled();
  // Show JotCoach gutter if either feature is enabled
  const shouldShowJotCoachGutter = isJotCoachEnabled || isAuditEnabled;

  const debug = forceDebug || useContext(DebugContext);

  const { getAccessToken } = useAccessToken();
  const userState = useUserState();
  const { preferences: userPreferences } = useUserPreferences();
  const { state: uistate } = useUIState();
  const { loadedInChromeExtension } = uistate;
  const { currentStep } = useTutorial();
  const { showDeleteModal: showRevertModal, hideDeleteModal: hideRevertModal } = useDeleteModal();

  const [content, setContent] = useState(context);
  const [showContext, setShowContext] = useState<boolean>(false);
  const [showEdits, setShowEdits] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedText, setEditedText] = useState<string>(text);
  const [showRevert, setShowRevert] = useState<boolean>(isEdited);
  const [lastSavedText, setLastSavedText] = useState<string>(text);
  const [isNA, setIsNA] = useState<boolean>(editedText.startsWith('N/A') ? true : false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [markedRated, setMarkedRated] = useState<boolean | '0' | '1'>(isRated ? true : false);
  const [commentSubmitted, setCommentSubmitted] = useState<boolean>(false);
  const [hideCommentBox, setHideCommentBox] = useState<boolean>(false);

  const [copyingState, setCopyingState] = useState<'idle' | 'copying' | 'success' | 'error'>(
    'idle',
  );

  // Compliance rule indicator state
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const indicatorRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const closeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Attestation edit indicator state
  const [dismissedAttestationEditIds, setDismissedAttestationEditIds] = useState<Set<string>>(
    new Set(),
  );
  const [hoveredAttestationEditIndex, setHoveredAttestationEditIndex] = useState<number | null>(
    null,
  );
  const attestationEditIndicatorRefs = useRef<{ [key: number]: HTMLElement | null }>({});
  const attestationEditCloseTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Bracket replacement popover state
  const [bracketMatch, setBracketMatch] = useState<BracketMatch | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const [editInstructions, setEditInstructions] = useState<string>('');
  const [awaitingEditTaskId, setAwaitingEditTaskId] = useState<string>();
  const [editTaskResult, setEditTaskResult] = useState<{
    result_text: string;
    diffs: any;
  }>();
  const [awaitingRegenerationTaskId, setAwaitingRegenerationTaskId] = useState<string>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const componentIsMounted = useRef(false);
  const noteTextRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const editModeRef = useRef<HTMLDivElement | null>(null);
  const editWithAIRef = useRef<HTMLDivElement | null>(null);
  const pollingIntervalId = useRef<number | null>(null);
  const regenerationIntervalRef = useRef<number | null>(null);
  const troubleWords = useTroubleWords();
  const plainTextViewRef = useRef<HTMLDivElement | null>(null);
  const sectionContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRenderedFiredRef = useRef<boolean>(false);
  const editStartedAtRef = useRef<number | null>(null);

  const INTERACTION_THRESHOLD_MS = 2000; // 2 seconds

  // Fetch compliance rules from API
  const { rules: allRules } = useJotCoachRules();

  // Get compliance rules from complianceRuleIds
  const complianceRules = useMemo(() => {
    if (
      !complianceRuleIds ||
      complianceRuleIds.length === 0 ||
      !allRules ||
      allRules.length === 0
    ) {
      return [];
    }

    // Map rule IDs to compliance rules
    const ruleIdSet = new Set(complianceRuleIds);
    return allRules.filter(rule => ruleIdSet.has(rule.id));
  }, [complianceRuleIds, allRules]);

  // Filter out dismissed attestation edits
  const visibleAttestationEdits = useMemo(() => {
    if (!attestationEdits || attestationEdits.length === 0) {
      return [];
    }
    return attestationEdits.filter(
      (_, index) => !dismissedAttestationEditIds.has(`${sectionId}-${index}`),
    );
  }, [attestationEdits, dismissedAttestationEditIds, sectionId]);

  // Handle compliance rule indicator hover
  const cancelCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  }, []);

  const handleIndicatorEnter = useCallback(
    (ruleId: string) => {
      cancelCloseTimeout();
      setHoveredRuleId(ruleId);
    },
    [cancelCloseTimeout],
  );

  const handleIndicatorLeave = useCallback(() => {
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverMouseEnter = useCallback(() => {
    cancelCloseTimeout();
  }, [cancelCloseTimeout]);

  const handlePopoverMouseLeave = useCallback(() => {
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverClose = useCallback(() => {
    cancelCloseTimeout();
    setHoveredRuleId(null);
  }, [cancelCloseTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      cancelCloseTimeout();
    };
  }, [cancelCloseTimeout]);

  const hoveredRule = hoveredRuleId
    ? complianceRules.find(rule => rule.id === hoveredRuleId)
    : null;

  const complianceRuleAnchorEl = hoveredRuleId ? indicatorRefs.current[hoveredRuleId] : null;

  // Handle attestation edit indicator hover
  const cancelAttestationEditCloseTimeout = useCallback(() => {
    if (attestationEditCloseTimeoutRef.current) {
      clearTimeout(attestationEditCloseTimeoutRef.current);
      attestationEditCloseTimeoutRef.current = undefined;
    }
  }, []);

  const handleAttestationEditIndicatorEnter = useCallback(
    (index: number) => {
      cancelAttestationEditCloseTimeout();
      setHoveredAttestationEditIndex(index);
    },
    [cancelAttestationEditCloseTimeout],
  );

  const handleAttestationEditIndicatorLeave = useCallback(() => {
    cancelAttestationEditCloseTimeout();
    attestationEditCloseTimeoutRef.current = setTimeout(() => {
      setHoveredAttestationEditIndex(null);
    }, 150);
  }, [cancelAttestationEditCloseTimeout]);

  const handleAttestationEditPopoverMouseEnter = useCallback(() => {
    cancelAttestationEditCloseTimeout();
  }, [cancelAttestationEditCloseTimeout]);

  const handleAttestationEditPopoverMouseLeave = useCallback(() => {
    cancelAttestationEditCloseTimeout();
    attestationEditCloseTimeoutRef.current = setTimeout(() => {
      setHoveredAttestationEditIndex(null);
    }, 150);
  }, [cancelAttestationEditCloseTimeout]);

  const handleAttestationEditPopoverClose = useCallback(() => {
    cancelAttestationEditCloseTimeout();
    setHoveredAttestationEditIndex(null);
  }, [cancelAttestationEditCloseTimeout]);

  // Handle accept action
  const handleAcceptAttestationEdit = useCallback(
    async (editIndex: number) => {
      const edit = visibleAttestationEdits[editIndex];
      if (!edit || !onAppendText) {
        return;
      }

      // Get current section content and append new text with single newline
      let currentContent: string;
      if (markdownMode && mainEditorRef.current) {
        // For markdown mode, get content from editor
        currentContent = await mainEditorRef.current.blocksToMarkdownLossy(
          mainEditorRef.current.document,
        );
      } else {
        // For plain text mode, use editedText
        currentContent = editedText.trim();
      }
      const newContent = currentContent ? `${currentContent}\n${edit.text}` : edit.text;

      // Call append handler
      await onAppendText(sectionId || '', newContent);

      // Dismiss the edit
      setDismissedAttestationEditIds(prev => new Set(prev).add(`${sectionId}-${editIndex}`));
      setHoveredAttestationEditIndex(null);
    },
    [visibleAttestationEdits, onAppendText, sectionId, markdownMode, editedText],
  );

  // Handle reject action
  const handleRejectAttestationEdit = useCallback(
    (editIndex: number) => {
      setDismissedAttestationEditIds(prev => new Set(prev).add(`${sectionId}-${editIndex}`));
      setHoveredAttestationEditIndex(null);
    },
    [sectionId],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      cancelAttestationEditCloseTimeout();
    };
  }, [cancelAttestationEditCloseTimeout]);

  // Handle bracket click - open popover
  const handleBracketClick = useCallback((match: BracketMatch, event: MouseEvent) => {
    setBracketMatch(match);
    setPopoverAnchor(event.target as HTMLElement);
  }, []);

  // Handle bracket replacement
  const handleBracketReplace = useCallback(
    async (replacementText: string) => {
      if (!mainEditorRef.current || !bracketMatch) return;

      try {
        // Access the underlying TipTap/ProseMirror instance
        /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
        const tiptapEditor = (mainEditorRef.current as any)._tiptapEditor;
        if (!tiptapEditor || !tiptapEditor.view) {
          console.warn('BlockNote editor TipTap instance not available');
          return;
        }

        const { state, dispatch } = tiptapEditor.view;
        const { tr } = state;

        // Replace the bracket text with the replacement text (without brackets)
        // If replacement is empty, remove the bracket entirely
        // Otherwise, use the replacement text as-is (no brackets)
        const trimmedReplacement = replacementText.trim();
        const replacement = trimmedReplacement;

        tr.replaceWith(bracketMatch.from, bracketMatch.to, state.schema.text(replacement));
        dispatch(tr);
        /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

        // Get updated markdown from editor and save
        if (mainEditorRef.current) {
          try {
            const updatedMarkdown = await mainEditorRef.current.blocksToMarkdownLossy(
              mainEditorRef.current.document,
            );
            // Update editedText state and save if handleSaveEdits is available
            setEditedText(updatedMarkdown);
            if (handleSaveEdits) {
              handleSaveEdits(updatedMarkdown);
            }
          } catch (saveError) {
            console.error('Error saving bracket replacement:', saveError);
          }
        }

        // Close popover
        setPopoverAnchor(null);
        setBracketMatch(null);
      } catch (error) {
        console.error('Error replacing bracket text:', error);
      }
    },
    [bracketMatch, handleSaveEdits],
  );

  // Handle bracket popover close
  const handleBracketPopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setBracketMatch(null);
  }, []);

  const hoveredAttestationEdit =
    hoveredAttestationEditIndex !== null
      ? visibleAttestationEdits[hoveredAttestationEditIndex]
      : null;

  const attestationEditAnchorEl =
    hoveredAttestationEditIndex !== null
      ? attestationEditIndicatorRefs.current[hoveredAttestationEditIndex]
      : null;

  // Markdown editor instance ref (only used when markdownMode === true)
  // Stored via onEditorReady callback from EditorView
  const mainEditorRef = useRef<BlockNoteEditor | null>(null);

  // admin-only, context editing feature
  const [editedContext, setEditedContext] = useState<string | undefined>(context);

  const [isContextEditMode, setIsContextEditMode] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(text === '');

  const [showAIEditBox, setShowAIEditBox] = useState(false);
  const [showAIEditTrainingTooltip, setShowAIEditTrainingTooltip] = useState(false);

  const handleContextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContext(event.target.value);
  };

  const toggleContextEditMode = () => {
    setIsContextEditMode(prev => !prev);
  };

  // const handleRegenerateWithEditedContext = () => {
  //   setIsRegenerating(true);
  //   handleRegenerateSection && handleRegenerateSection(editedContext);
  // };

  const toggleEditMode = (
    source: 'button' | 'double_click' | 'mobile_menu' | 'feedback_auto_edit' = 'button',
  ) => {
    const wasEditMode = isEditMode;
    setIsEditMode(!isEditMode);

    // Track edit mode changes
    if (!wasEditMode) {
      // Entering edit mode
      // Track interaction event (user initiated edit from various sources)
      noteSectionMetrics.editClicked({
        section_id: sectionId,
        section_type: heading,
        section_name: heading,
        source: source,
        note_id: noteId,
        encounter_id: encounterId,
      });
      editStartedAtRef.current = performance.now();
    } else {
      // Exiting edit mode - check if there were changes
      const hasChanges = editedText !== stripTags(text);
      if (hasChanges) {
        noteSectionMetrics.editCancelled({
          section_id: sectionId,
          section_type: heading,
          had_changes: true,
        });
      }
      editStartedAtRef.current = null;
    }

    if (setTutorialText) {
      setTutorialText(
        'Make an edit',
        "The note section is now a text editor. Want to edit faster? Just type what you want to do into the AI edit bar beneath the note section. If you write your instruction with phrases like 'always include' or 'never', JotPsych will learn your preferences for next time. Give it a try!",
      );
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(event.target.value);
  };

  // Split the text by newlines and map each non-empty line to a <p> element
  const paragraphs = editedText.split('\n').map((line, i) => {
    if (line) {
      return textToParagraph({ text: line, className: 'py-2' });
    }
  });

  // Handles blur event on the textarea
  const handleBlur = (event: FocusEvent) => {
    // Check if the new focused element is outside the editModeRef container
    // and not within the EditWithAI component
    setTimeout(() => {
      if (
        editModeRef.current &&
        !editModeRef.current.contains(event.relatedTarget as Node) &&
        editWithAIRef.current &&
        !editWithAIRef.current.contains(event.relatedTarget as Node)
      ) {
        setIsEditMode(false);
      }
    }, 10); // A short delay
  };

  //Handles blur event on the textarea
  // const handleBlur = (event: FocusEvent) => {
  //   // Check if the new focused element is outside the editModeRef container
  //   setTimeout(() => {
  //     if (
  //       editModeRef.current &&
  //       !editModeRef.current.contains(event.relatedTarget as Node)
  //     ) {
  //       setIsEditMode(false);
  //     }
  //   }, 10); // A short delay
  // };

  const handleCopy = async () => {
    try {
      // Track interaction event (user clicked Copy button)
      noteSectionMetrics.copyClicked({
        section_id: sectionId,
        section_type: heading,
        section_name: heading,
        note_id: noteId,
        encounter_id: encounterId,
      });

      setCopyingState('copying');

      let htmlContent: string;
      let plainText: string;

      if (markdownMode) {
        // Markdown mode: convert raw stored markdown directly to HTML/plain text.
        // We intentionally bypass the BlockNote editor here because its block model
        // has normalizeMarkdownNewlines() baked in, which promotes single newlines
        // to double newlines. Using the raw editedText preserves original spacing.
        const includeHtml =
          (userPreferences as any)?.copy_paste_preferences?.html_formatting_on_copy ?? true;

        let sectionHtml: string;
        let sectionPlainText: string;
        try {
          sectionHtml = await marked(editedText);
          sectionPlainText = removeMd(editedText, {
            stripListLeaders: false,
            useImgAltText: false,
          });
        } catch (e) {
          console.error('Error parsing markdown for copy:', e);
          sectionHtml = `<div style="white-space: pre-wrap;">${editedText.replace(/\n/g, '<br/>')}</div>`;
          sectionPlainText = editedText;
        }

        const success = await copyRichText(sectionHtml, sectionPlainText, includeHtml);

        if (success) {
          setCopyingState('success');
          enqueueSnackbar('Section copied to clipboard', { variant: 'success' });

          // Track copy event (existing telemetry)
          noteSectionMetrics.copied({
            section_id: sectionId,
            section_type: heading,
            copy_method: 'button',
            include_formatting: includeHtml,
          });

          // Track copy event (new telemetry model)
          const context: TelemetryContext = {
            docId: analyticsDocId,
            getDocVersion,
          };
          buildSelectionCopied({
            context,
            lenSelected: editedText.length,
            lenTotal: editedText.length,
            modality: 'copy_button',
            metadata: {
              ...analyticsMetadata,
              section_title: heading,
            },
          });

          if (handleSectionSetCopyStatus) {
            handleSectionSetCopyStatus();
          }
          if (handleSectionSendCopyEvents && moduleId) {
            handleSectionSendCopyEvents(moduleId);
          }

          // Reset to idle after 2 seconds
          setTimeout(() => {
            setCopyingState('idle');
          }, 2000);
        } else {
          setCopyingState('error');
          enqueueSnackbar('Failed to copy section', { variant: 'error' });
          setTimeout(() => {
            setCopyingState('idle');
          }, 2000);
        }
        return;
      } else {
        // Plain text mode: create HTML formatted text for rich text copying
        htmlContent = text
          .split('\n')
          .map(line => (line ? `<p>${line}</p>` : '<br/>'))
          .join('');
        plainText = stripTags(text);
      }

      // Get HTML formatting preference (default to true)
      const includeHtml =
        (userPreferences as any)?.copy_paste_preferences?.html_formatting_on_copy ?? true;

      const success = await copyRichText(htmlContent, plainText, includeHtml);

      if (success) {
        setCopyingState('success');
        enqueueSnackbar('Section copied to clipboard', { variant: 'success' });

        // Track copy event (existing telemetry)
        noteSectionMetrics.copied({
          section_id: sectionId,
          section_type: heading,
          copy_method: 'button',
          include_formatting: includeHtml,
        });

        // Track copy event (new telemetry model)
        const totalText = editedText || text;
        const context: TelemetryContext = {
          docId: analyticsDocId,
          getDocVersion,
        };
        buildSelectionCopied({
          context,
          lenSelected: plainText.length,
          lenTotal: totalText.length,
          modality: 'copy_button',
          metadata: {
            ...analyticsMetadata,
            section_title: heading,
          },
        });

        if (handleSectionSetCopyStatus) {
          handleSectionSetCopyStatus();
        }
        if (handleSectionSendCopyEvents && moduleId) {
          handleSectionSendCopyEvents(moduleId);
        }

        // Reset to idle after 2 seconds
        setTimeout(() => {
          setCopyingState('idle');
        }, 2000);
      } else {
        setCopyingState('error');
        enqueueSnackbar('Failed to copy section', { variant: 'error' });
        setTimeout(() => {
          setCopyingState('idle');
        }, 2000);
      }
    } catch (error) {
      console.error('Error copying content:', error);
      setCopyingState('error');
      enqueueSnackbar('Failed to copy section', { variant: 'error' });
      setTimeout(() => {
        setCopyingState('idle');
      }, 2000);
    }
  };

  const handleSendPaste = () => {
    console.log('Sending paste instructions to chrome extension...');
    window.parent.postMessage(
      {
        type: 'pasteText',
        text: text,
      },
      '*',
    );
  };

  const handleSubmitCommentFeedback = (comment: string, ai_instruction_session_id: string) => {
    setCommentSubmitted(true);
    setTimeout(() => {
      setHideCommentBox(true);
    }, 3000);

    // pass comment to parent component
    if (handleSectionCommentFeedback) {
      handleSectionCommentFeedback(comment, ai_instruction_session_id);
    }
  };

  const handleEditWithAISubmit = async (editInstructions: string) => {
    if (handleRequestAIEdit) {
      // Track AI edit request
      noteSectionMetrics.aiEditRequested({
        section_id: sectionId,
        section_type: heading,
        instruction_length: editInstructions.length,
      });

      // Get current text - either from BlockNote or from editedText state
      let currentText = editedText;
      if (markdownMode && mainEditorRef.current) {
        currentText = await mainEditorRef.current.blocksToMarkdownLossy(
          mainEditorRef.current.document,
        );
      }

      handleRequestAIEdit(
        currentText,
        editInstructions,
        moduleId ? moduleId : heading,
        (taskId: string) => {
          setAwaitingEditTaskId(taskId);
          setEditTaskResult(undefined);
        },
      );
    }
  };

  const checkEditTaskStatus = async (taskId: string) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      return;
    }

    try {
      const response = await APIService.makeAPIGetRequest({
        requestString: `/notes/getEditSectionWithAITaskStatus?task_id=${taskId}`,
        accessToken: accessToken,
      });
      if (response.ok && response.value.status === 200) {
        if (pollingIntervalId.current) {
          clearInterval(pollingIntervalId.current);
        }
        setAwaitingEditTaskId(undefined); // Clear the taskId
        setEditTaskResult(response.value.task_result); // Store the result

        // Track AI instruction rendered event
        noteSectionMetrics.aiInstructionRendered({
          section_id: sectionId,
          section_name: heading,
          note_id: noteId,
          encounter_id: encounterId,
          result_length: response.value.task_result.result_text?.length,
          has_diffs: !!response.value.task_result.diffs,
        });
      }
    } catch (error) {
      console.error('Failed to check task status:', error);
      if (pollingIntervalId.current) {
        clearInterval(pollingIntervalId.current);
      }
    }
  };

  const getRegenerateSectionTaskStatus = async (taskId: string) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await APIService.makeAPIGetRequest({
        requestString: `/notes/getSectionRegenerationTaskStatus?task_id=${taskId}`,
        accessToken: accessToken,
      });
      console.log(response);
      if (response.ok && response.value.status !== 202) {
        console.log(response.value.task_result);
        if (regenerationIntervalRef.current) {
          clearInterval(regenerationIntervalRef.current);
        }
        setAwaitingRegenerationTaskId(undefined); // Clear the taskId

        const newText = response.value.task_result.text_result;
        const beforeLen = lastSavedText.length;
        const afterLen = newText.length;

        // Track section_edited event before updating state
        buildSectionEdited({
          context: sectionContext,
          beforeLen,
          afterLen,
          source: 'regeneration',
          metadata: {
            ...analyticsMetadata,
            section_title: heading,
          },
        });

        // Update markdown editor if in markdown mode
        if (markdownMode && mainEditorRef.current) {
          const blocks = await mainEditorRef.current.tryParseMarkdownToBlocks(newText);
          mainEditorRef.current.replaceBlocks(mainEditorRef.current.document, blocks);
        }

        setEditedText(newText);
        setLastSavedText(newText);
        if (handleSaveEdits) {
          handleSaveEdits(newText);
          // Track regeneration saved (existing telemetry)
          noteSectionMetrics.editSaved({
            section_id: sectionId,
            section_type: heading,
            has_changes: true,
            edit_source: 'regeneration',
          });
        }
        setIsRegenerating(false);
      }
    } catch (error) {
      console.error('Failed to check task status:', error);
      setIsRegenerating(false);
      if (regenerationIntervalRef.current) {
        clearInterval(regenerationIntervalRef.current);
      }
    }
  };

  const highlightTroubleWords = (text: string) => {
    if (!text) return text;

    if (typeof troubleWords !== 'object' || troubleWords === null) {
      console.error('troubleWords is not a valid object:', troubleWords);
      return text;
    }

    const allMisspellings: string[] = troubleWords.reduce((acc, current) => {
      return [...acc, ...current.misspellings];
    }, [] as string[]);

    // Ensure misspellings are strings and log any non-string values
    allMisspellings.forEach((word, index) => {
      if (typeof word !== 'string') {
        console.error(`Non-string value at index ${index}:`, word);
      }
    });

    // Helper function to escape special regex characters
    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const misspellingsPattern = allMisspellings
      .filter(word => typeof word === 'string') // Filter out non-string values
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|');

    // Validate pattern before creating regex
    if (!misspellingsPattern || misspellingsPattern.trim() === '') {
      console.warn('highlightTroubleWords: No valid misspellings pattern found');
      return text;
    }

    let regex: RegExp;
    try {
      // Create regex with error handling
      regex = new RegExp(
        `(?<=^|\\s|[.,!?;:()\\[\\]\\-/'"])(${misspellingsPattern})(?=\\s|[.,!?;:()\\[\\]\\-/'"]|$)`,
        'gi',
      );
    } catch (error) {
      console.error(
        'highlightTroubleWords: Failed to create regex with lookbehind, falling back to simpler pattern:',
        error,
      );
      try {
        // Fallback: simpler regex without lookbehind assertions for better browser compatibility
        regex = new RegExp(`\\b(${misspellingsPattern})\\b`, 'gi');
      } catch (fallbackError) {
        console.error('highlightTroubleWords: Fallback regex also failed:', fallbackError);
        return text; // Return original text if regex creation fails
      }
    }

    // Split the text into lines
    const lines = text.split('\n');

    // Process each line with error handling
    const processedLines = lines.map((line, lineIndex) => {
      try {
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(line)) !== null) {
          const [, matchedWord] = match; // The actual match is in the first capturing group
          const matchIndex = match.index + (match[0].length - matchedWord.length); // Adjust for lookbehind

          if (matchIndex > lastIndex) {
            parts.push(line.slice(lastIndex, matchIndex));
          }

          parts.push(
            <span
              key={`${lineIndex}-${matchIndex}`}
              style={{ backgroundColor: '#FFE4E1', cursor: 'pointer' }}
              onClick={() => {
                if (onWordClick) onWordClick(matchedWord);
              }}
            >
              {matchedWord}
            </span>,
          );

          lastIndex = matchIndex + matchedWord.length;
        }

        if (lastIndex < line.length) {
          parts.push(line.slice(lastIndex));
        }

        // Wrap the processed line in a paragraph tag
        return (
          <p key={`line-${lineIndex}`} className="py-2">
            {parts}
          </p>
        );
      } catch (error) {
        console.error(`highlightTroubleWords: Error processing line ${lineIndex}:`, error);
        // Return line as plain text if processing fails
        return (
          <p key={`line-${lineIndex}`} className="py-2">
            {line}
          </p>
        );
      }
    });

    return processedLines;
  };

  const handleDoubleClick = () => {
    if (!isEditMode) {
      toggleEditMode('double_click');
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 10);
    }
  };

  const handleAcceptAIEdits = async () => {
    if (editTaskResult) {
      // Track interaction event (user clicked Accept button)
      noteSectionMetrics.aiEditAcceptClicked({
        section_id: sectionId,
        section_type: heading,
        section_name: heading,
        edit_mode: 'ai_edit',
        note_id: noteId,
        encounter_id: encounterId,
      });

      const beforeLen = lastSavedText.length;
      const afterLen = editTaskResult.result_text.length;

      // Track section_edited event before updating state
      buildSectionEdited({
        context: sectionContext,
        beforeLen,
        afterLen,
        source: 'ai_edit',
        timings: editStartedAtRef.current ? { startedAt: editStartedAtRef.current } : undefined,
        metadata: {
          ...analyticsMetadata,
          section_title: heading,
        },
      });

      // Update markdown editor if in markdown mode
      if (markdownMode && mainEditorRef.current) {
        const blocks = await mainEditorRef.current.tryParseMarkdownToBlocks(
          editTaskResult.result_text,
        );
        mainEditorRef.current.replaceBlocks(mainEditorRef.current.document, blocks);
      }

      setEditedText(editTaskResult.result_text);
      setEditTaskResult(undefined);
      setEditInstructions('');
      setLastSavedText(editTaskResult.result_text);
      if (handleSaveEdits) {
        handleSaveEdits(editTaskResult.result_text);
        // Track AI edit saved (existing telemetry)
        noteSectionMetrics.editSaved({
          section_id: sectionId,
          section_type: heading,
          has_changes: true,
          edit_source: 'ai_edit',
        });
      }
      editStartedAtRef.current = null;

      // Use a timeout to allow the component to re-render before adjusting the textarea
      // setTimeout(() => {
      //   if (textAreaRef.current) {
      //     textAreaRef.current.style.height = 'auto';
      //     textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      //   }
      // }, 0);
    }
  };

  const handleRejectAIEdits = () => {
    // Track interaction event (user clicked Reject button)
    noteSectionMetrics.aiEditRejectClicked({
      section_id: sectionId,
      section_type: heading,
      section_name: heading,
      edit_mode: 'ai_edit',
      note_id: noteId,
      encounter_id: encounterId,
    });

    setEditTaskResult(undefined);
    setEditInstructions('');

    // Use a timeout to allow the component to re-render before adjusting the textarea
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.style.height = 'auto';
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      }
    }, 0);
  };

  const pollEditTaskStatusfromAIInstructions = (taskId: string) => {
    setAwaitingEditTaskId(taskId);
  };

  const handleThumbsDown = () => {
    if (markedRated === false) {
      setMarkedRated('0');
      handleSectionRatingFeedback && handleSectionRatingFeedback(0);
      setShowAIEditBox(true);
      setShowAIEditTrainingTooltip(true);
      toggleEditMode('feedback_auto_edit');
    }
  };

  const handleDoneEditing = async () => {
    // Track interaction event (user clicked Done button)
    const editDuration = editStartedAtRef.current
      ? performance.now() - editStartedAtRef.current
      : undefined;

    if (markdownMode && mainEditorRef.current && isEditMode) {
      // Get markdown from the editor
      const markdown = await mainEditorRef.current.blocksToMarkdownLossy(
        mainEditorRef.current.document,
      );

      const hadChanges = markdown !== lastSavedText;

      // Track Done click interaction
      noteSectionMetrics.editDoneClicked({
        section_id: sectionId,
        section_type: heading,
        section_name: heading,
        had_changes: hadChanges,
        edit_duration_ms: editDuration,
        note_id: noteId,
        encounter_id: encounterId,
      });

      // Only save if content has changed
      if (hadChanges) {
        const beforeLen = lastSavedText.length;
        const afterLen = markdown.length;

        // Track section_edited event before updating state
        buildSectionEdited({
          context: sectionContext,
          beforeLen,
          afterLen,
          source: 'manual',
          timings: editStartedAtRef.current ? { startedAt: editStartedAtRef.current } : undefined,
          metadata: {
            ...analyticsMetadata,
            section_title: heading,
          },
        });

        setLastSavedText(markdown);
        setEditedText(markdown);
        if (handleSaveEdits) {
          handleSaveEdits(markdown);
          // Track manual edit saved (existing telemetry)
          noteSectionMetrics.editSaved({
            section_id: sectionId,
            section_type: heading,
            has_changes: true,
            edit_source: 'manual',
          });
        }
        editStartedAtRef.current = null;
      }
    } else {
      // Track Done click for non-markdown mode or when not in edit mode
      const hadChanges = editedText !== stripTags(text);
      noteSectionMetrics.editDoneClicked({
        section_id: sectionId,
        section_type: heading,
        section_name: heading,
        had_changes: hadChanges,
        edit_duration_ms: editDuration,
        note_id: noteId,
        encounter_id: encounterId,
      });
    }

    setIsEditMode(false);
    setShowAIEditBox(false);
    setShowAIEditTrainingTooltip(false);
  };

  // Handle editor ready callback - store editor reference
  const handleEditorReady = useCallback(
    (editor: BlockNoteEditor) => {
      mainEditorRef.current = editor;
      if (onEditorReady) {
        onEditorReady(editor);
      }
    },
    [onEditorReady],
  );

  // Track document version for analytics (increments when editedText changes)
  const docVersionRef = useRef<number>(0);
  const lastEditedTextRef = useRef<string>(editedText);
  useEffect(() => {
    if (lastEditedTextRef.current !== editedText) {
      docVersionRef.current += 1;
      lastEditedTextRef.current = editedText;
    }
  }, [editedText]);

  // Analytics configuration
  const analyticsDocId =
    noteId && sectionId ? `note:${noteId}:section:${sectionId}` : `section:${heading}`;
  const plainAnalyticsDocId =
    noteId && sectionId ? `note:${noteId}:section:${sectionId}:plain` : `section:${heading}:plain`;
  const getDocVersion = useCallback(() => docVersionRef.current.toString(), []);
  const analyticsMetadata = {
    module_id: moduleId,
    section_id: sectionId,
    note_id: noteId,
    encounter_id: encounterId,
  };

  // Track section_rendered event (when section enters viewport)
  const sectionContext: TelemetryContext = {
    docId: analyticsDocId,
    getDocVersion,
  };
  useSectionRendered(sectionContainerRef, {
    context: sectionContext,
    getActiveSection: {
      id: sectionId || heading,
      title: heading,
    },
    metadata: {
      ...analyticsMetadata,
      section_title: heading,
    },
    onSectionRendered: () => {
      sectionRenderedFiredRef.current = true;
    },
  });

  // Track section_interacted event (fires on mouse leave if duration >= 2 seconds)
  useSectionInteracted(sectionContainerRef, {
    context: sectionContext,
    getActiveSection: {
      id: sectionId || heading,
      title: heading,
    },
    thresholdMs: INTERACTION_THRESHOLD_MS,
    metadata: {
      ...analyticsMetadata,
      section_title: heading,
    },
  });

  // Track text_selected and text_copied events within this section
  // Expose helpers for BlockNote selection analytics to call
  useEffect(() => {
    const container = sectionContainerRef.current;
    if (!container) return;

    // Store helpers on container so BlockNote selection analytics can call them
    // These will increment counts in the shared useSectionInteracted hook
    const currentSectionId = sectionId || heading;
    (container as any).__noteSectionTrackSelect = () => {
      if ((container as any).__sectionAnalytics?.trackTextSelected) {
        (container as any).__sectionAnalytics.trackTextSelected(currentSectionId);
      }
    };

    (container as any).__noteSectionTrackCopy = () => {
      if ((container as any).__sectionAnalytics?.trackTextCopied) {
        (container as any).__sectionAnalytics.trackTextCopied(currentSectionId);
      }
    };

    return () => {
      delete (container as any).__noteSectionTrackSelect;
      delete (container as any).__noteSectionTrackCopy;
    };
  }, [markdownMode, sectionId, heading]);

  // Analytics for plain text view mode (when not in markdown mode)
  useDomSelectionAnalytics(
    !markdownMode ? plainTextViewRef : { current: null },
    !markdownMode
      ? {
          docId: plainAnalyticsDocId,
          getDocVersion,
          getPlainText: () => editedText,
          metadata: analyticsMetadata,
          onEvent: trackNoteTelemetry,
        }
      : {
          docId: plainAnalyticsDocId,
          getDocVersion,
          getPlainText: () => '',
          metadata: analyticsMetadata,
          onEvent: trackNoteTelemetry,
        },
  );

  // Analytics for plain text edit mode (textarea)
  useTextareaSelectionAnalytics(
    !markdownMode && isEditMode ? textAreaRef : { current: null },
    !markdownMode && isEditMode
      ? {
          docId: plainAnalyticsDocId,
          getDocVersion,
          getPlainText: () => editedText,
          metadata: analyticsMetadata,
          onEvent: trackNoteTelemetry,
        }
      : {
          docId: plainAnalyticsDocId,
          getDocVersion,
          getPlainText: () => '',
          metadata: analyticsMetadata,
          onEvent: trackNoteTelemetry,
        },
  );

  useEffect(() => {
    if (awaitingEditTaskId) {
      pollingIntervalId.current = setInterval(() => {
        checkEditTaskStatus(awaitingEditTaskId);
      }, 3000) as unknown as number;
    }
    return () => {
      if (pollingIntervalId.current) {
        clearInterval(pollingIntervalId.current);
      }
    };
  }, [awaitingEditTaskId]);

  useEffect(() => {
    if (awaitingRegenerationTaskId) {
      regenerationIntervalRef.current = setInterval(() => {
        getRegenerateSectionTaskStatus(awaitingRegenerationTaskId); // only line getting an error, cant find this func anywhere in the code base either. didnt make any changes here
      }, 3000) as unknown as number;
    }
    return () => {
      if (regenerationIntervalRef.current) {
        clearInterval(regenerationIntervalRef.current);
      }
    };
  }, [awaitingRegenerationTaskId]);

  useEffect(() => {
    // Add event listeners to manage focus within the editModeRef container
    const container = editModeRef.current;
    if (container) {
      container.addEventListener('focusout', handleBlur);
    }
    return () => {
      if (container) {
        container.removeEventListener('focusout', handleBlur);
      }
    };
  }, []);

  useEffect(() => {
    if (textAreaRef.current) {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textAreaRef.current.style.height = '0px';

      let height = 0;

      if (isEditMode && noteTextRef.current) {
        height = noteTextRef.current.scrollHeight;
      } else {
        height = textAreaRef.current.scrollHeight;
      }

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      textAreaRef.current.style.height = height + 'px';
    }

    if (editedText.startsWith('N/A')) {
      setIsNA(true);
    } else {
      setIsNA(false);
    }
  }, [editedText, isEditMode]);

  useEffect(() => {
    if (componentIsMounted.current) {
      setIsLoading(text === '');
      setEditedText(text);
      setLastSavedText(text);
      setShowRevert(isEdited);

      // EditorView will handle content updates via its markdown prop
    }
  }, [text, isEdited, markdownMode]);

  useEffect(() => {
    if (isEditMode) {
      editModeRef.current?.focus();
    } else if (componentIsMounted.current) {
      // only run when isEditMode becomes false and not on the initial render
      editModeRef.current?.blur();

      // only save text to server if it has changed
      if (editedText !== lastSavedText) {
        const beforeLen = lastSavedText.length;
        const afterLen = editedText.length;

        // Track section_edited event before updating state
        buildSectionEdited({
          context: sectionContext,
          beforeLen,
          afterLen,
          source: 'manual',
          timings: editStartedAtRef.current ? { startedAt: editStartedAtRef.current } : undefined,
          metadata: {
            ...analyticsMetadata,
            section_title: heading,
          },
        });

        setLastSavedText(editedText);
        if (handleSaveEdits) {
          handleSaveEdits(editedText);
          // Track manual edit saved (existing telemetry)
          noteSectionMetrics.editSaved({
            section_id: sectionId,
            section_type: heading,
            has_changes: true,
            edit_source: 'manual',
          });
        }
        editStartedAtRef.current = null;
      }
    }
    componentIsMounted.current = true; // set this to true after the initial render
  }, [isEditMode]);

  return (
    <Box>
      <Box sx={{ marginBottom: 4, display: 'flex' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box className="flex justify-between items-center">
            <Box className="text-xl pb-2 md:text-2xl">
              <Box id="note-section-heading" className="inline flex items-center">
                {!isNA && !isMobile && (
                  <Box className="inline flex items-center">
                    <TutorialStep
                      step={startingTutorialStep ? startingTutorialStep + 0 : 0}
                      description="Many JotPsych users copy their notes to other EHRs. You can copy a section directly, or press the '+' button to select it for copying when you press the 'Copy Selected Sections' button in the top toolbar."
                      placement={'left'}
                      enabled={!!startingTutorialStep}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <ConditionalTooltip
                          title="This button selects a section to be copied when you press 'Copy Selected Sections' in the sidebar."
                          condition={showTooltips}
                          placement={'left'}
                          arrow
                        >
                          <>
                            {!isAdded && (
                              <Box
                                onClick={() => {
                                  handleAdd();
                                  // Track section added
                                  noteSectionMetrics.added({
                                    section_id: sectionId,
                                    section_type: heading,
                                    add_method: 'button',
                                  });
                                }}
                                sx={{
                                  mx: 1,
                                  ':hover': { cursor: 'pointer' },
                                }}
                              >
                                <SVGIcon name="add" />
                              </Box>
                            )}
                            {isAdded && (
                              <Box
                                onClick={() => {
                                  handleAdd();
                                  // Track section removed
                                  noteSectionMetrics.removed({
                                    section_id: sectionId,
                                    section_type: heading,
                                    remove_reason: 'user_action',
                                  });
                                }}
                                sx={{
                                  mx: 1,
                                  ':hover': { cursor: 'pointer' },
                                }}
                              >
                                <SVGIcon name="check-filled" />
                              </Box>
                            )}
                          </>
                        </ConditionalTooltip>

                        <ConditionalTooltip
                          title="Copy this section to your clipboard."
                          condition={showTooltips}
                          placement={'left'}
                          arrow
                        >
                          <Box
                            onClick={handleCopy}
                            sx={{
                              mx: 1,
                              ':hover': { cursor: 'pointer' },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px',
                            }}
                          >
                            {copyingState === 'idle' && <SVGIcon name="copy" />}
                            {copyingState === 'copying' && (
                              <CircularProgress
                                size={20}
                                sx={{ color: theme.palette.primary.main }}
                              />
                            )}
                            {copyingState === 'success' && (
                              <Check sx={{ color: theme.palette.success.main }} />
                            )}
                            {copyingState === 'error' && <SVGIcon name="copy" />}
                          </Box>
                        </ConditionalTooltip>
                      </Box>
                    </TutorialStep>
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    textTransform: 'capitalize',
                    pl: 2,
                    fontWeight: 'bold',
                    color: isNA ? 'textColors.disabled' : 'textColors.darkText',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {/* Remove any text between parentheses, e.g. (Customized) */}
                  {heading?.replace(/\s*\([^)]*\)/g, '')}
                </Typography>
                {/* Compliance rule indicators - horizontal row next to heading */}
                {(isJotCoachEnabled || isAuditEnabled) && complianceRules.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '4px',
                      ml: 1,
                      pointerEvents: 'auto',
                    }}
                  >
                    {complianceRules.map(rule => (
                      <Box
                        key={rule.id}
                        ref={el => {
                          indicatorRefs.current[rule.id] = el as HTMLElement | null;
                        }}
                      >
                        <ComplianceRuleIndicator
                          rule={rule}
                          isHovered={hoveredRuleId === rule.id}
                          onMouseEnter={() => handleIndicatorEnter(rule.id)}
                          onMouseLeave={handleIndicatorLeave}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <TutorialStep
              step={startingTutorialStep ? startingTutorialStep + 1 : 0}
              description="You can further customize and train your scribe by providing feedback and editing the section. Use the 'edit' button, or double click the section text to enter edit mode."
              placement={'left'}
              enabled={!!startingTutorialStep}
            >
              <Box className="flex items-center flex-wrap">
                {isEditable &&
                  !isMobile &&
                  sectionId &&
                  handleSectionRatingFeedback &&
                  !isRated &&
                  !hideCommentBox && (
                    <ConditionalTooltip
                      title="These buttons give feedback on a section. This feedback is used to further improve your personal scribe."
                      condition={showTooltips}
                      placement={'left'}
                      arrow
                    >
                      <Box className="flex items-center mr-4">
                        <Box
                          onClick={handleThumbsDown}
                          sx={{ mx: 1, ':hover': { cursor: 'pointer' } }}
                        >
                          {markedRated !== '0' && (
                            <ThumbDownIconAlt htmlColor={theme.palette.primary.light} />
                          )}
                          {markedRated && markedRated == '0' && (
                            <ThumbDownIconFilled htmlColor={theme.palette.primary.light} />
                          )}
                        </Box>
                        <Box
                          onClick={() => {
                            if (markedRated === false) {
                              setMarkedRated('1');
                              handleSectionRatingFeedback(1);
                            }
                          }}
                          sx={{ mx: 1, ':hover': { cursor: 'pointer' } }}
                        >
                          {markedRated !== '1' && (
                            <ThumbUpIconAlt htmlColor={theme.palette.primary.light} />
                          )}
                          {markedRated && markedRated == '1' && (
                            <ThumbUpIconFilled htmlColor={theme.palette.primary.light} />
                          )}
                        </Box>
                      </Box>
                    </ConditionalTooltip>
                  )}
                {isEditable &&
                  !isMobile &&
                  handleRevertEdits &&
                  showRevert &&
                  !isSaving &&
                  !editTaskResult && (
                    <ConditionalTooltip
                      title="Discard your changes and revert the section to its original generation."
                      condition={showTooltips}
                      placement={'top'}
                      arrow
                    >
                      <RoundedButton
                        variant="contained"
                        onClick={() => {
                          showRevertModal({
                            isOpen: true,
                            title: 'Revert Edits?',
                            children: (
                              <Typography>
                                Are you sure you want to revert the edits on this section? This
                                action cannot be undone.
                              </Typography>
                            ),
                            continueText: 'Revert',
                            cancelText: 'Cancel',
                            onCancel: () => hideRevertModal(),
                            onContinue: () => {
                              // Track interaction event (user clicked Revert in modal)
                              noteSectionMetrics.revertClicked({
                                section_id: sectionId,
                                section_type: heading,
                                section_name: heading,
                                note_id: noteId,
                                encounter_id: encounterId,
                              });
                              handleRevertEdits();
                              // Track revert outcome event
                              noteSectionMetrics.reverted({
                                section_id: sectionId,
                                section_type: heading,
                                revert_reason: 'user_action',
                              });
                              hideRevertModal();
                            },
                          });
                        }}
                      >
                        Revert Changes
                      </RoundedButton>
                    </ConditionalTooltip>
                  )}
                {!isMobile && handleSaveEdits && !isEditMode && !isSaving && (
                  <ConditionalTooltip
                    title={
                      isEditable
                        ? ''
                        : "This note is marked as complete. Press 'Review and Edit' below to make changes to this section."
                    }
                    condition={!isEditable}
                    placement="top"
                    arrow
                  >
                    <span>
                      <RoundedButton
                        variant="outlined"
                        onClick={() => toggleEditMode('button')}
                        disabled={!isEditable}
                      >
                        Edit
                      </RoundedButton>
                    </span>
                  </ConditionalTooltip>
                )}

                {handleSaveEdits && !isEditMode && isSaving && (
                  <Box className="max-w-sm" sx={{ color: 'textColors.lightText' }}>
                    Saving...
                  </Box>
                )}
                {handleSaveEdits && isEditMode && !editTaskResult && (
                  <RoundedButton variant="contained" onClick={() => handleDoneEditing()}>
                    Done
                  </RoundedButton>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 0,
                  }}
                >
                  {loadedInChromeExtension && (
                    <Tooltip title={showChromeExtensionTooltips ? 'Paste to your EHR' : ''}>
                      <Button
                        id="basic-button"
                        aria-controls={menuOpen ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={menuOpen ? 'true' : undefined}
                        onClick={handleSendPaste}
                      >
                        <ContentPasteGo />
                      </Button>
                    </Tooltip>
                  )}
                  {moduleId && (
                    <>
                      {isMobile && (
                        <Button
                          id="basic-button"
                          aria-controls={menuOpen ? 'basic-menu' : undefined}
                          aria-haspopup="true"
                          aria-expanded={menuOpen ? 'true' : undefined}
                          onClick={handleMenuClick}
                        >
                          <MoreHoriz />
                        </Button>
                      )}
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose} slotProps={{ list: {
                          'aria-labelledby': 'basic-button',
                        } }}
                      >
                        {isEditable &&
                          isMobile &&
                          sectionId &&
                          handleSectionRatingFeedback &&
                          !isRated &&
                          !hideCommentBox && (
                            <MenuItem onClick={handleMenuClose}>
                              <Box className="flex items-center">
                                <Button
                                  onClick={() => {
                                    if (markedRated === false) {
                                      setMarkedRated('0');
                                      handleSectionRatingFeedback(0);
                                    }
                                  }}
                                  className="px-1"
                                >
                                  {markedRated !== '0' && (
                                    <ThumbDownIconAlt htmlColor={theme.palette.primary.light} />
                                  )}
                                  {markedRated && markedRated == '0' && (
                                    <ThumbDownIconFilled htmlColor={theme.palette.primary.light} />
                                  )}
                                </Button>
                                <Button
                                  onClick={() => {
                                    if (markedRated === false) {
                                      setMarkedRated('1');
                                      handleSectionRatingFeedback(1);
                                    }
                                  }}
                                  className="px-1"
                                >
                                  {markedRated !== '1' && (
                                    <ThumbUpIconAlt htmlColor={theme.palette.primary.light} />
                                  )}
                                  {markedRated && markedRated == '1' && (
                                    <ThumbUpIconFilled htmlColor={theme.palette.primary.light} />
                                  )}
                                </Button>
                              </Box>
                            </MenuItem>
                          )}
                        {isMobile && !isEditMode && !isSaving && (
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              handleCopy();
                            }}
                          >
                            Copy Section
                          </MenuItem>
                        )}
                        {isEditable &&
                          isMobile &&
                          handleRevertEdits &&
                          showRevert &&
                          !isSaving &&
                          !editTaskResult && (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                // Show the revert modal
                                showRevertModal({
                                  isOpen: true,
                                  title: 'Revert Edits?',
                                  children: (
                                    <Typography>
                                      Are you sure you want to revert the edits on this section?
                                      This action cannot be undone.
                                    </Typography>
                                  ),
                                  continueText: 'Revert',
                                  cancelText: 'Cancel',
                                  onCancel: () => hideRevertModal(),
                                  onContinue: () => {
                                    // Track interaction event (user clicked Revert in modal)
                                    noteSectionMetrics.revertClicked({
                                      section_id: sectionId,
                                      section_type: heading,
                                      section_name: heading,
                                      note_id: noteId,
                                      encounter_id: encounterId,
                                    });
                                    handleRevertEdits();
                                    // Track revert outcome event
                                    noteSectionMetrics.reverted({
                                      section_id: sectionId,
                                      section_type: heading,
                                      revert_reason: 'user_action',
                                    });
                                    hideRevertModal();
                                  },
                                });
                              }}
                            >
                              Revert Changes
                            </MenuItem>
                          )}
                        {isEditable && isMobile && handleSaveEdits && !isEditMode && !isSaving && (
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              toggleEditMode('mobile_menu');
                            }}
                          >
                            Edit
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </TutorialStep>
          </Box>

          {/* EditWithAI - appears above content and sidebar */}
          {(isEditMode || showAIEditBox || currentStep === startingTutorialStep! + 2) && (
            <ConditionalTooltip
              title="Train your scribe by providing specific feedback on how to improve this section."
              condition={showAIEditBox}
              placement="bottom"
              arrow
              open={showAIEditTrainingTooltip}
            >
              <TutorialStep
                step={startingTutorialStep! + 2}
                description="While in edit mode, you can train your scribe by providing specific feedback on how to improve this section."
                placement="bottom"
                enabled={!!startingTutorialStep}
              >
                <Box sx={{ mt: 1 }} onFocus={() => setShowAIEditTrainingTooltip(false)}>
                  <EditWithAI
                    editInstructions={editInstructions}
                    setEditInstructions={setEditInstructions}
                    onEditInstructionsSubmit={handleEditWithAISubmit}
                    loading={awaitingEditTaskId ? true : false}
                    handleStartModuleCustomizationSession={handleStartModuleCustomizationSession}
                    sectionContent={editedText}
                    moduleId={moduleId || ''}
                    pollEditTaskStatusfromAIInstructions={pollEditTaskStatusfromAIInstructions}
                    handleSubmitCommentFeedback={
                      showAIEditBox ? handleSubmitCommentFeedback : undefined
                    }
                    sectionId={sectionId}
                    encounterId={encounterId}
                    sectionName={heading}
                  />
                </Box>
              </TutorialStep>
            </ConditionalTooltip>
          )}

          {/* {markedRated && !isRated && (
                <Box className="p-1">
                  {!commentSubmitted && (
                    <InlineCommentBox onSubmit={handleSumbitCommentFeedback} />
                  )}
                  {commentSubmitted && !hideCommentBox && (
                    <Typography color="textColors.primaryText">
                      Thank you for your feedback!
                    </Typography>
                  )}
                </Box>
              )} */}

          <Box
            ref={sectionContainerRef}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: 'flex-start',
              gap: shouldShowJotCoachGutter ? 2 : 0,
              position: 'relative',
            }}
          >
            {/* Left gutter for attestation indicators */}
            {shouldShowJotCoachGutter && (
              <Box
                sx={{
                  width: { xs: '100%', lg: '64px' },
                  minWidth: { lg: '64px' },
                  display: 'flex',
                  flexDirection: { xs: 'row', lg: 'column' },
                  justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                  alignItems: { xs: 'center', lg: 'center' },
                  gap: 0.5,
                  pointerEvents: 'auto',
                  alignSelf: { xs: 'stretch', lg: 'stretch' },
                  order: { xs: 2, lg: 1 },
                }}
              >
                {visibleAttestationEdits.map((edit, visibleIndex) => {
                  // Find the original index in attestationEdits array by matching the edit object
                  // This is needed because we need to track dismissed edits by their original index
                  const originalIndex =
                    attestationEdits?.findIndex((e, idx) => {
                      // Match by comparing all fields
                      return (
                        e.note_section === edit.note_section &&
                        e.rationale === edit.rationale &&
                        e.rule === edit.rule &&
                        e.text === edit.text &&
                        e.title === edit.title
                      );
                    }) ?? visibleIndex;

                  return (
                    <Box
                      key={`${sectionId}-attestation-${visibleIndex}`}
                      ref={(el: HTMLElement | null) => {
                        attestationEditIndicatorRefs.current[originalIndex] = el;
                      }}
                      sx={{
                        opacity: 1,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                    >
                      <AttestationEditIndicator
                        isHovered={hoveredAttestationEditIndex === originalIndex}
                        onMouseEnter={() => {
                          handleAttestationEditIndicatorEnter(originalIndex);
                        }}
                        onMouseLeave={handleAttestationEditIndicatorLeave}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                width: { xs: '100%', lg: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                order: { xs: 1, lg: 2 },
              }}
            >
              <Box
                className="flex-auto mt-2"
                ref={editModeRef}
                onDoubleClick={isEditable ? handleDoubleClick : undefined}
              >
                {isErrored && (
                  <Box className="p-4 border-1 border-gray-200 border-dashed rounded-lg  w-full resize-none overflow-hidden leading-7 text-center">
                    <Typography color="textColors.primaryText">
                      Error generating section.
                    </Typography>
                    {handleRegenerateSection && (
                      <RoundedButton
                        onClick={() => {
                          setIsRegenerating(true);
                          // Track regeneration request
                          noteSectionMetrics.regenerated({
                            section_id: sectionId,
                            section_type: heading,
                            regeneration_reason: 'error_recovery',
                          });
                          handleRegenerateSection(
                            moduleId ? moduleId : heading,
                            (taskId: string) => {
                              setAwaitingRegenerationTaskId(taskId);
                            },
                          );
                        }}
                      >
                        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                      </RoundedButton>
                    )}
                  </Box>
                )}
                {/* Markdown Mode Rendering */}
                {markdownMode && !isErrored && (
                  <ConditionalTooltip
                    title={
                      isEditable
                        ? "This is the markdown content for this note section. You can press the 'edit' button to make changes."
                        : ''
                    }
                    condition={showTooltips && isEditable}
                    placement={'left'}
                    arrow
                  >
                    <Box
                      sx={{
                        pt: 4,
                        border: 1,
                        borderRadius: '0.75rem',
                        color: isNA ? 'textColors.disabled' : 'textColors.darkText',
                        borderColor: isNA ? 'borderColors.lightprimary' : 'borderColors.primary',
                        bgcolor: isNA ? 'backgroundColors.secondary' : 'backgroundColors.primary',
                        position: 'relative',
                        overflow: 'hidden',
                        opacity: isNA ? 0.7 : 1,
                      }}
                    >
                      {isLoading ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 4,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          {/* AI Edit Result UI */}
                          {editTaskResult && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                AI Suggested Changes:
                              </Typography>
                              <Box
                                sx={{
                                  mb: 2,
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'center',
                                  gap: 2,
                                }}
                              >
                                <RoundedButton variant="contained" onClick={handleAcceptAIEdits}>
                                  Accept Changes
                                </RoundedButton>

                                <RoundedButton variant="contained" onClick={handleRejectAIEdits}>
                                  Reject Changes
                                </RoundedButton>
                              </Box>
                              <Box
                                sx={{
                                  p: 2,
                                  border: '2px dashed',
                                  borderColor: 'primary.light',
                                  borderRadius: '0.5rem',
                                  bgcolor: 'backgroundColors.secondary',
                                }}
                              >
                                <Typography
                                  dangerouslySetInnerHTML={{
                                    __html: editTaskResult.result_text,
                                  }}
                                />
                              </Box>
                            </Box>
                          )}

                          {/* Editor View - handles both edit and view modes */}
                          {!editTaskResult && (
                            <Box
                              onDoubleClick={
                                isEditable && !isEditMode
                                  ? () => toggleEditMode('double_click')
                                  : undefined
                              }
                              sx={{
                                width: '100%',
                                opacity: isNA ? 0.7 : 1,
                              }}
                            >
                              <EditorView
                                markdown={editedText}
                                editable={isEditMode}
                                enableMisspelling={!!misspelledWords && misspelledWords.length > 0}
                                misspelledWords={misspelledWords}
                                onWordClick={onWordClick}
                                troubleWords={troubleWords}
                                enableBracketHighlight={enableBracketHighlight}
                                onBracketClick={
                                  enableBracketHighlight ? handleBracketClick : onBracketClick
                                }
                                onEditorReady={handleEditorReady}
                                styleId={`note-section-editor-${sectionId || 'default'}`}
                                analytics={{
                                  enabled: true,
                                  docId: analyticsDocId,
                                  getDocVersion,
                                  metadata: analyticsMetadata,
                                }}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </ConditionalTooltip>
                )}

                {/* Plain Text Mode Rendering */}
                {!markdownMode && !isEditMode && !isErrored && (
                  <ConditionalTooltip
                    title={
                      isEditable
                        ? "This is the generated text for this note section. You can double click or press the 'edit' button to make changes."
                        : ''
                    }
                    condition={showTooltips && isEditable}
                    placement={'left'}
                    arrow
                  >
                    <Box
                      ref={plainTextViewRef}
                      sx={{
                        p: 4,
                        border: 1,
                        borderRadius: '0.75rem',
                        color: isNA ? 'textColors.disabled' : 'textColors.darkText',
                        borderColor: 'borderColors.primary',
                        bgcolor: 'backgroundColors.primary',
                      }}
                    >
                      {isLoading && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      )}
                      {!isLoading && highlightTroubleWords(editedText)}
                    </Box>
                  </ConditionalTooltip>
                )}
                {!markdownMode && isEditable && isEditMode && (
                  <>
                    {editTaskResult && (
                      <>
                        <Box
                          ref={noteTextRef}
                          // onDoubleClick={handleSaveEdits ? toggleEditMode : () => {}}
                          sx={{
                            p: 4,
                            mb: 1,
                            border: 2,
                            borderStyle: 'dashed',
                            borderRadius: '0.75rem',
                            color: isNA ? 'textColors.disabled' : 'textColors.darkText',
                            borderColor: isNA
                              ? 'borderColors.lightprimary'
                              : 'borderColors.primary',
                            bgcolor: isNA ? 'backgroundColors.primary' : Colors.grey100,
                          }}
                        >
                          {/* {editTaskResult.diffs.map((diff: any, index: number) =>
                        renderDiff(diff)
                      )} */}
                          <Box
                            sx={{
                              mb: 2,
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                            }}
                          >
                            <RoundedButton variant="contained" onClick={handleAcceptAIEdits}>
                              Accept Changes
                            </RoundedButton>

                            <RoundedButton variant="contained" onClick={handleRejectAIEdits}>
                              Reject Changes
                            </RoundedButton>
                          </Box>
                          {renderDiffs(editTaskResult.diffs)}
                        </Box>
                      </>
                    )}
                    {!editTaskResult && (
                      <textarea
                        ref={textAreaRef}
                        className="p-4 md:p-8 border-1 rounded-lg w-full resize-none overflow-hidden leading-7 md:leading-8"
                        style={{
                          ...{
                            color: isNA ? Colors.grey500 : Colors.grey700,
                          },
                          ...{
                            backgroundColor: isNA ? Colors.primary100 : Colors.grey100,
                          },
                          ...{ display: isEditMode ? 'initial' : 'none' },
                          ...{ borderColor: Colors.primary200 },
                        }}
                        value={editedText}
                        onChange={handleTextChange}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Compliance rule popover */}
      {(isJotCoachEnabled || isAuditEnabled) && hoveredRule && (
        <ComplianceRulePopover
          rule={hoveredRule}
          anchorEl={complianceRuleAnchorEl}
          open={Boolean(hoveredRule && complianceRuleAnchorEl)}
          onClose={handlePopoverClose}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        />
      )}

      {/* Attestation edit popover */}
      {(isJotCoachEnabled || isAuditEnabled) &&
        hoveredAttestationEdit &&
        hoveredAttestationEditIndex !== null && (
          <AttestationEditPopover
            attestationEdit={hoveredAttestationEdit}
            anchorEl={attestationEditAnchorEl}
            open={Boolean(hoveredAttestationEdit && attestationEditAnchorEl)}
            onClose={handleAttestationEditPopoverClose}
            onAccept={() => handleAcceptAttestationEdit(hoveredAttestationEditIndex)}
            onReject={() => handleRejectAttestationEdit(hoveredAttestationEditIndex)}
            onMouseEnter={handleAttestationEditPopoverMouseEnter}
            onMouseLeave={handleAttestationEditPopoverMouseLeave}
          />
        )}

      {/* Bracket replacement popover */}
      {enableBracketHighlight && (
        <BracketReplacementPopover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          bracketMatch={bracketMatch}
          onClose={handleBracketPopoverClose}
          onReplace={handleBracketReplace}
        />
      )}

      {debug && (
        <Box>
          <RoundedButton
            variant="outlined"
            sx={{ color: 'textColors.primaryText' }}
            className="ml-20 font-medium text-sm px-5 py-2.5 mr-2 mb-2 text-center"
            onClick={() => {
              setShowContext(!showContext);
            }}
          >
            Show Context
          </RoundedButton>
          {handleRegenerateSection && (
            <RoundedButton
              variant="outlined"
              sx={{ color: 'textColors.primaryText' }}
              className="ml-20 font-medium text-sm px-5 py-2.5 mr-2 mb-2 text-center"
              onClick={() => {
                setIsRegenerating(true);
                // Track regeneration request
                noteSectionMetrics.regenerated({
                  section_id: sectionId,
                  section_type: heading,
                  regeneration_reason: 'user_requested',
                });
                handleRegenerateSection(moduleId ? moduleId : heading, (taskId: string) => {
                  setAwaitingRegenerationTaskId(taskId);
                });
              }}
            >
              {isRegenerating ? 'Regenerating...' : 'Regenerate (CAREFUL: PERMANENT)'}
            </RoundedButton>
          )}
          {edits && (
            <RoundedButton
              variant="outlined"
              sx={{ color: 'textColors.primaryText' }}
              className="ml-20 font-medium text-sm px-5 py-2.5 mr-2 mb-2 text-center"
              onClick={() => {
                setShowEdits(!showEdits);
              }}
            >
              Show Edits
            </RoundedButton>
          )}
          {/* debug print of context sections */}
          {showContext && (
            <Box className="my-4 px-20">
              {isContextEditMode ? (
                <textarea
                  className="p-4 border-1 rounded-lg w-full leading-7"
                  style={{ borderColor: Colors.primary200 }}
                  value={editedContext}
                  onChange={handleContextChange}
                  onBlur={() => setIsContextEditMode(false)}
                />
              ) : (
                <Box
                  className="p-4 border-1 rounded-lg"
                  onDoubleClick={toggleContextEditMode}
                  style={{
                    backgroundColor: Colors.primary100,
                    color: Colors.grey700,
                  }}
                >
                  {editedContext?.split('\n').map(el => {
                    if (el) {
                      return <p key={el}>{el}</p>;
                    }
                    return null;
                  })}
                </Box>
              )}
            </Box>
          )}
          {edits && showEdits && (
            <Box className="my-4 px-20">
              {edits
                .slice()
                .reverse()
                .map((edit: any) => (
                  <EditDiffComponent diffs={edit.diff} timestamp={edit.timestamp} />
                ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
